import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mail, Sparkles, Loader2, Copy, Eye, Code } from "lucide-react";

export default function NewsletterTemplates() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("warm");
  const [audience, setAudience] = useState("");
  const [cta, setCta] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [showHtml, setShowHtml] = useState(false);

  useEffect(() => { if (!loading && (!user || user === false)) nav("/login"); }, [user, loading, nav]);

  const gen = async () => {
    if (topic.trim().length < 2) { toast.error("What's the newsletter about?"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/marketing/newsletter", { subject_topic: topic, tone, audience, include_cta: cta }, { timeout: 60000 });
      setResult(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const copy = (txt) => {
    try {
      navigator.clipboard.writeText(txt).then(() => toast.success("Copied")).catch(() => toast.error("Copy failed"));
    } catch { toast.error("Copy failed"); }
  };

  if (loading || !user) return null;
  return (
    <>
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6">
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]"><Mail className="w-3.5 h-3.5" style={{ color: "#2F80ED" }} /><span>Marketing tool</span></div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] mt-1">Email <span style={{ color: "#2F80ED" }}>Newsletter</span> Templates</h1>
          <p className="mt-2 text-[#857163]">Generate ready-to-send newsletter drafts for customers and market announcements.</p>
        </div>
        <div className="cp-card p-5 mb-6 space-y-3">
          <label><span className="text-xs text-[#857163]">What's the newsletter about?</span><input className="cp-input mt-1" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="This weekend's cake pop flavors + upcoming farmers market" data-testid="topic" /></label>
          <div className="grid sm:grid-cols-2 gap-3">
            <label><span className="text-xs text-[#857163]">Tone</span><select className="cp-input mt-1" value={tone} onChange={(e) => setTone(e.target.value)} data-testid="tone"><option value="warm">Warm & personal</option><option value="playful">Playful</option><option value="professional">Professional</option><option value="urgent">Urgent / limited</option></select></label>
            <label><span className="text-xs text-[#857163]">Audience</span><input className="cp-input mt-1" value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Local customers, order-by-DM" data-testid="audience" /></label>
          </div>
          <label className="flex items-center gap-2"><input type="checkbox" checked={cta} onChange={(e) => setCta(e.target.checked)} className="w-4 h-4" data-testid="cta" /><span className="text-sm text-[#3B2C24]">Include call-to-action button</span></label>
          <button onClick={gen} disabled={busy || !topic.trim()} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white font-semibold disabled:opacity-50" style={{ background: "linear-gradient(135deg, #2F80ED, #1B5FBB)" }} data-testid="gen-btn">
            {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Writing…</> : <><Sparkles className="w-5 h-5" /> Generate newsletter</>}
          </button>
        </div>
        {result && (
          <div className="cp-card p-5 space-y-3" data-testid="newsletter-result">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#857163] mb-1">Subject line</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 font-semibold text-[#3B2C24]">{result.subject_line}</div>
                <button onClick={() => copy(result.subject_line)} className="p-1.5 rounded-full hover:bg-[#F9EFE6]"><Copy className="w-3.5 h-3.5" /></button>
              </div>
              {result.preview_text && <div className="text-xs text-[#857163] mt-0.5">Preview: {result.preview_text}</div>}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs uppercase tracking-widest text-[#857163]">Body</div>
                <div className="flex gap-1">
                  <button onClick={() => setShowHtml(false)} className={`px-2 py-0.5 rounded-full text-xs ${!showHtml ? "bg-[#3B2C24] text-white" : "bg-white border border-[#EBDCD1]"}`}><Eye className="w-3 h-3 inline" /> Preview</button>
                  <button onClick={() => setShowHtml(true)} className={`px-2 py-0.5 rounded-full text-xs ${showHtml ? "bg-[#3B2C24] text-white" : "bg-white border border-[#EBDCD1]"}`}><Code className="w-3 h-3 inline" /> HTML</button>
                  <button onClick={() => copy(showHtml ? result.body_html : result.body_plain)} className="p-1 rounded-full hover:bg-[#F9EFE6]"><Copy className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              {showHtml ? (
                <pre className="bg-[#F9EFE6] p-3 rounded-2xl text-[11px] font-mono whitespace-pre-wrap overflow-x-auto max-h-[500px] overflow-y-auto">{result.body_html}</pre>
              ) : (
                <div className="bg-white border border-[#EBDCD1] rounded-2xl p-4 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: result.body_html || result.body_plain || "" }} />
              )}
            </div>
          </div>
        )}
      </main>
    </>
  );
}
